/**
 * The URL contract, in one module.
 *
 * Every tab, filter, drawer, modal and wizard step a person can link to is a
 * route or a search param, so a link reproduces the screen and Back undoes the
 * last thing opened. This file is where the address-bar half of that lives.
 *
 * AN OVERLAY THAT LIVES IN THE URL follows one rule:
 *
 * - OPENING pushes a history entry and marks it `openedHere`.
 * - CLOSING goes back one entry when the overlay was opened here, and otherwise
 *   replaces the URL without the param. So Back closes what was opened here,
 *   and closing an overlay that arrived in a pasted link leaves no duplicate
 *   entry, no way for Back to bring it back, and no way for Back to leave the
 *   app altogether.
 *
 * There used to be two hooks for this, one written for the shell's two panels
 * (`?sheet=shortcuts`, `?modal=workspace`) and one for the overlays, and they
 * disagreed on their return shape. This is the one that is left. It takes an
 * optional bound value for a param that several overlays share, which is what
 * the shell's version existed for: `?modal=workspace` must not report open, or
 * close, while the param says `?modal=invite`.
 *
 * The decisions are plain functions over strings and state, tested without a
 * router. `useOverlayParam` is the thin hook that carries them out.
 *
 * FILTERS use one shape on every page (`f.<category>`, `x.<category>`, flags,
 * `rule=`). The model lives beside the filter panel in `components/`, and the
 * address-bar functions are re-exported here so the contract has one import.
 */
import { useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export { filterParams, parseFilterParams, writeFilterParams } from '../components/filter-model'
export type { FilterSchema, FilterValue } from '../components/filter-model'

/** The history state an overlay opened from inside the app leaves behind. */
export interface OverlayHistoryState {
  openedHere?: boolean
  [key: string]: unknown
}

function asState(state: unknown): OverlayHistoryState {
  return state !== null && typeof state === 'object' ? (state as OverlayHistoryState) : {}
}

/** `search` with `name` set to `value`, or removed when `value` is null. Keeps a leading `?` only when something is left. */
export function withSearchParam(search: string, name: string, value: string | null): string {
  const params = new URLSearchParams(search)
  if (value === null) params.delete(name)
  else params.set(name, value)
  const next = params.toString()
  return next === '' ? '' : `?${next}`
}

export type OverlayOpenPlan = { search: string; state: OverlayHistoryState } | null

/**
 * What opening does: a push, marked as opened here. Null when the param
 * already holds that value, because pushing a second identical entry would
 * make Back look broken.
 */
export function planOverlayOpen(search: string, name: string, value: string, state: unknown): OverlayOpenPlan {
  if (new URLSearchParams(search).get(name) === value) return null
  return { search: withSearchParam(search, name, value), state: { ...asState(state), openedHere: true } }
}

export type OverlayClosePlan =
  | { kind: 'back' }
  | { kind: 'replace'; search: string; state: OverlayHistoryState }
  | null

/**
 * What closing does. Back when this entry was pushed by opening the overlay,
 * a replace without the param when it was not, and nothing at all when the
 * param is already gone (a second close in the same tick, from Escape and a
 * scrim click racing).
 *
 * With `bound`, only that value counts as this overlay: a param holding some
 * other value belongs to a different overlay, and closing this one leaves it
 * alone.
 */
export function planOverlayClose(search: string, name: string, state: unknown, bound?: string): OverlayClosePlan {
  const params = new URLSearchParams(search)
  if (!params.has(name)) return null
  if (bound !== undefined && params.get(name) !== bound) return null
  const current = asState(state)
  if (current.openedHere === true) return { kind: 'back' }
  const rest: OverlayHistoryState = { ...current }
  delete rest.openedHere
  return { kind: 'replace', search: withSearchParam(search, name, null), state: rest }
}

/** Whether the overlay named by `name`, and by `bound` when given, is showing. */
export function overlayValueIsOpen(value: string | null, bound?: string): boolean {
  return bound === undefined ? value !== null : value === bound
}

export interface OverlayParam {
  /** The param's value, or null while the overlay is shut. */
  value: string | null
  isOpen: boolean
  /**
   * Opens the overlay. `value` defaults to the bound value, or to `1` for an
   * overlay with nothing to name.
   */
  open: (value?: string) => void
  close: () => void
}

/**
 * Binds an overlay to a search param:
 *
 * ```tsx
 * const contact = useOverlayParam('contact')
 * <Drawer open={contact.isOpen} onClose={contact.close} ...>
 *
 * const shortcuts = useOverlayParam('sheet', 'shortcuts')
 * ```
 *
 * `open` and `close` keep one identity across renders. They read the location
 * through a ref, so they can be handed to a context or a key binding without
 * re-rendering every consumer each time the address changes.
 */
export function useOverlayParam(name: string, bound?: string): OverlayParam {
  const location = useLocation()
  const navigate = useNavigate()
  const latest = useRef(location)
  latest.current = location

  const open = useCallback(
    (next?: string) => {
      const here = latest.current
      const plan = planOverlayOpen(here.search, name, next ?? bound ?? '1', here.state)
      if (plan === null) return
      navigate({ pathname: here.pathname, search: plan.search, hash: here.hash }, { state: plan.state })
    },
    [bound, name, navigate],
  )

  const close = useCallback(() => {
    const here = latest.current
    const plan = planOverlayClose(here.search, name, here.state, bound)
    if (plan === null) return
    if (plan.kind === 'back') {
      navigate(-1)
      return
    }
    navigate(
      { pathname: here.pathname, search: plan.search, hash: here.hash },
      { replace: true, state: plan.state },
    )
  }, [bound, name, navigate])

  const value = new URLSearchParams(location.search).get(name)
  return { value, isOpen: overlayValueIsOpen(value, bound), open, close }
}
